import React, { useEffect, useRef, useState } from 'react';
import './RichTextEditor.css';

const FONT_SIZES = ['8pt', '9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt', '20pt', '22pt', '24pt', '26pt', '28pt', '32pt', '36pt', '48pt', '60pt', '72pt'];

const FONT_FAMILIES = [
  { label: 'Helvetica / Arial', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Edwardian Script', value: '"Edwardian Script ITC", "Great Vibes", cursive' },
  { label: 'Courier New', value: '"Courier New", monospace' }
];

const COLOR_PRESETS = [
  { label: 'Zwart', value: '#000000' },
  { label: 'Donkergrijs', value: '#374151' },
  { label: 'Grijs', value: '#6b7280' },
  { label: 'NLBA paars', value: '#6b1d6e' },
  { label: 'NLBA oranje', value: '#e8501e' },
  { label: 'Rood', value: '#b91c1c' },
  { label: 'Blauw', value: '#1d4ed8' },
  { label: 'Groen', value: '#15803d' }
];

const RichTextEditor = ({ label, value, onChange, placeholder, minHeight = 180 }) => {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [textColor, setTextColor] = useState('#000000');
  const [highlightColor, setHighlightColor] = useState('#fff59d');

  // Alleen de inhoud overschrijven als die van buitenaf verandert, zodat de cursor niet verspringt tijdens het typen.
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && editor.innerHTML !== (value || '')) {
      editor.innerHTML = value || '';
    }
  }, [value]);

  // Onthoud de laatste selectie in de editor; dropdowns en kleurkiezers halen de focus weg.
  useEffect(() => {
    const handleSelectionChange = () => {
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (editor.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const restoreSelection = () => {
    const editor = editorRef.current;
    if (!editor) return false;
    editor.focus();
    const range = savedRangeRef.current;
    if (!range) return false;
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  };

  const emitChange = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const runCommand = (command, commandValue = null, useCss = false) => {
    if (!editorRef.current) return;
    restoreSelection();
    document.execCommand('styleWithCSS', false, useCss);
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  // execCommand kent alleen grootte 1-7; markeer met 7 en vervang daarna door een exacte puntgrootte.
  const applyFontSize = (size) => {
    if (!size || !editorRef.current) return;
    restoreSelection();
    document.execCommand('styleWithCSS', false, false);
    document.execCommand('fontSize', false, '7');
    editorRef.current.querySelectorAll('font[size="7"]').forEach((fontEl) => {
      const span = document.createElement('span');
      span.style.fontSize = size;
      while (fontEl.firstChild) span.appendChild(fontEl.firstChild);
      fontEl.parentNode.replaceChild(span, fontEl);
    });
    emitChange();
  };

  const applyTextColor = (color) => {
    setTextColor(color);
    runCommand('foreColor', color, true);
  };

  const applyHighlight = (color) => {
    setHighlightColor(color);
    runCommand('hiliteColor', color, true);
  };

  const insertImage = () => {
    const imageUrl = window.prompt('Afbeelding URL plakken (of {{org.logoWide}} / {{org.logo}} voor het NLBA logo):');
    if (!imageUrl) return;
    runCommand('insertImage', imageUrl);
  };

  const insertLink = () => {
    const url = window.prompt('Link URL:');
    if (!url) return;
    runCommand('createLink', url);
  };

  // Voorkomt dat knoppen de selectie in de editor wegnemen.
  const keepSelection = (e) => e.preventDefault();

  const toolbarButton = (title, content, onClick) => (
    <button type="button" onMouseDown={keepSelection} onClick={onClick} className="rte-btn" title={title}>
      {content}
    </button>
  );

  return (
    <div className="rte-group">
      {label && <label className="rte-label">{label}</label>}
      <div className="rte-toolbar" role="toolbar" aria-label={`${label || 'Editor'} toolbar`}>
        <div className="rte-toolbar-row">
          {toolbarButton('Vet', <strong>B</strong>, () => runCommand('bold'))}
          {toolbarButton('Cursief', <i>I</i>, () => runCommand('italic'))}
          {toolbarButton('Onderstrepen', <u>U</u>, () => runCommand('underline'))}
          {toolbarButton('Kop 1', 'H1', () => runCommand('formatBlock', 'H1'))}
          {toolbarButton('Kop 2', 'H2', () => runCommand('formatBlock', 'H2'))}
          {toolbarButton('Paragraaf', 'P', () => runCommand('formatBlock', 'P'))}
          {toolbarButton('Links uitlijnen', 'Links', () => runCommand('justifyLeft'))}
          {toolbarButton('Centreren', 'Centreren', () => runCommand('justifyCenter'))}
          {toolbarButton('Rechts uitlijnen', 'Rechts', () => runCommand('justifyRight'))}
          {toolbarButton('Opsomming', '• Lijst', () => runCommand('insertUnorderedList'))}
          {toolbarButton('Nummering', '1. Lijst', () => runCommand('insertOrderedList'))}
          {toolbarButton('Link invoegen', 'Link', insertLink)}
          {toolbarButton('Afbeelding invoegen', 'Afbeelding', insertImage)}
          {toolbarButton('Opmaak wissen', 'Wissen', () => runCommand('removeFormat'))}
        </div>

        <div className="rte-toolbar-row">
          <label className="rte-inline-label">
            Grootte
            <select className="rte-btn" value="" onChange={(e) => applyFontSize(e.target.value)} title="Tekengrootte">
              <option value="">Kies…</option>
              {FONT_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>

          <label className="rte-inline-label">
            Lettertype
            <select className="rte-btn" value="" onChange={(e) => e.target.value && runCommand('fontName', e.target.value, true)} title="Lettertype">
              <option value="">Kies…</option>
              {FONT_FAMILIES.map((font) => (
                <option key={font.label} value={font.value}>{font.label}</option>
              ))}
            </select>
          </label>

          <label className="rte-inline-label" title="Tekstkleur">
            Tekstkleur
            <input
              type="color"
              className="rte-color-picker"
              value={textColor}
              onChange={(e) => applyTextColor(e.target.value)}
              aria-label="Tekstkleur"
            />
          </label>
          <div className="rte-swatches" aria-label="Snelle kleuren">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className="rte-swatch"
                style={{ background: preset.value }}
                title={preset.label}
                aria-label={preset.label}
                onMouseDown={keepSelection}
                onClick={() => applyTextColor(preset.value)}
              />
            ))}
          </div>

          <label className="rte-inline-label" title="Markeerkleur">
            Markeren
            <input
              type="color"
              className="rte-color-picker"
              value={highlightColor}
              onChange={(e) => applyHighlight(e.target.value)}
              aria-label="Markeerkleur"
            />
          </label>
        </div>
      </div>

      <div
        ref={editorRef}
        className="rte-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        data-placeholder={placeholder || ''}
        style={{ minHeight: `${minHeight}px` }}
      />
      <small className="rte-hint">Selecteer eerst tekst en kies daarna grootte, lettertype of kleur.</small>
    </div>
  );
};

export default RichTextEditor;
